import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Typography,
} from "@mui/material";
import { DataGrid, GridMoreVertIcon } from "@mui/x-data-grid";
import { ptBR } from "@mui/x-data-grid/locales";
import { saveAs } from "file-saver";
import ExcelJS from "exceljs";
import PlusIcon from "../../assets/Icons/components/Plus";

const ActionCell = ({ params, actions }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const hasVisibleActions = actions.some((action) =>
    action.isVisible?.(params.row)
  );

  return (
    <>
      <IconButton
        onClick={handleClick}
        sx={{
          opacity: hasVisibleActions ? 1 : 0,
          pointerEvents: hasVisibleActions ? "auto" : "none",
        }}
      >
        <GridMoreVertIcon fontSize="small" />
      </IconButton>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {actions
          .filter((action) => action.isVisible?.(params.row))
          .map((action, index) => (
            <MenuItem
              key={index}
              onClick={() => {
                action.action(params.row);
                handleClose();
              }}
            >
              {action.icon && (
                <span style={{ marginRight: 8 }}>{action.icon}</span>
              )}
              {action.tooltip}
            </MenuItem>
          ))}
      </Menu>
    </>
  );
};

const SheetsTable = ({
  data,
  loading,
  columns,
  actions,
  redirect,
  redirectText,
  tableName,
}) => {
  const [currentRows, setCurrentRows] = useState(data);
  const [showAll, setShowAll] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});

  useEffect(() => setCurrentRows(data), [loading, data]);

  // Adiciona coluna de ações
  const styledColumns = [
    ...columns,
    {
      field: "actions",
      headerName: "Ações",
      renderCell: (params) => <ActionCell params={params} actions={actions} />,
      disableColumnMenu: true,
      width: 80,
      sortable: false,
    },
  ].map((col) => {
    const isIconColumn = col.field === "icon" || (col.width && !col.flex);
    const originalRender = col.renderCell;
    return {
      ...col,
      headerClassName: "super-app-theme--header",
      align: isIconColumn ? "center" : col.align,
      headerName: isIconColumn ? "Tipo" : col.headerName,
      headerAlign: isIconColumn ? "center" : col.headerAlign,
      sortable: isIconColumn ? false : col.sortable,
      filterable: isIconColumn ? false : col.filterable,
      disableColumnMenu: isIconColumn ? true : col.disableColumnMenu,
      renderCell: isIconColumn
        ? (params) => (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 0,
                color: "inherit !important",
              }}
            >
              {originalRender ? originalRender(params) : params.value}
            </Box>
          )
        : originalRender,
    };
  });

  const countOculto = data.filter(
    (item) => item.Visibility === "Oculto"
  ).length;
  const activeRows = showAll
    ? currentRows
    : countOculto > 0
    ? data.filter((item) => item.Visibility !== "Oculto")
    : currentRows;
  const activeColumns = styledColumns;

  const skeletonRows = Array.from({ length: 10 }, (_, i) => ({ id: i }));
  const skeletonColumns = activeColumns.map((col) => ({
    ...col,
    headerClassName: "super-app-theme--header",
    headerAlign: "left",
    renderCell: () => (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height="100%"
      >
        <Skeleton
          sx={{ borderRadius: "8px" }}
          variant="rectangular"
          width="100%"
          height={20}
        />
      </Box>
    ),
  }));

  // Largura total calculada para permitir rolagem horizontal
  const totalColumnWidth = activeColumns.reduce(
    (sum, col) => sum + (col.width || 250),
    0
  );

  const showEverything = () => setShowAll(true);
  const removeShowEverything = () => setShowAll(false);

  const handleExportExcel = async (
    fileName = `${tableName || "dados"}.xlsx`
  ) => {
    // Filtra só colunas visíveis
    const visibleColumns = columns.filter(
      (col) => columnVisibilityModel[col.field] !== false
    );
    const headers = visibleColumns.map((col) => col.headerName || col.field);

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Dados");
    worksheet.columns = visibleColumns.map(() => ({ width: 20 }));
    worksheet.addRow(headers);

    // Filtra só linhas selecionadas (ou todas se nada selecionado)
    const rowsToExport =
      selectedRows.length > 0
        ? currentRows.filter((row) => selectedRows.includes(row.id))
        : activeRows;

    rowsToExport.forEach((row) => {
      const rowData = visibleColumns.map((col) => {
        const value = row[col.field];
        if (value === null || value === undefined) return "";
        if (typeof value === "object") return JSON.stringify(value);
        return value;
      });
      worksheet.addRow(rowData);
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: "application/octet-stream" }), fileName);
  };

  return (
    <Box
      sx={{
        padding: "20px",
        display: "flex",
        flexDirection: "column",
        gap: "20px",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "end", minWidth: "100%" }}>
        <Button
          variant="contained"
          sx={{ padding: "15px", height: "40px", marginLeft: "auto" }}
          startIcon={<PlusIcon />}
          onClick={() => redirect()}
        >
          {redirectText}
        </Button>
      </Box>

      {/* Tabela responsiva com rolagem horizontal */}
      <Box sx={{ width: "100%", overflowX: "auto" }}>
        <Box sx={{ minWidth: `${totalColumnWidth}px` }}>
          <DataGrid
            rows={loading ? skeletonRows : activeRows}
            columns={loading ? skeletonColumns : activeColumns}
            checkboxSelection
            rowSelectionModel={selectedRows}
            onRowSelectionModelChange={(ids) => setSelectedRows(ids)}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={(model) =>
              setColumnVisibilityModel(model)
            }
            localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
            initialState={{
              pagination: { paginationModel: { pageSize: 25, page: 0 } },
              density: "comfortable",
            }}
            getRowClassName={(params) =>
              params.row.Visibility === "Oculto" ? "row-oculto" : ""
            }
            sx={{
              backgroundColor: "#FFF",
              "& .row-oculto": {
                backgroundColor: "#fffbea", // cor de fundo diferente para ocultos
              },
            }}
          />
        </Box>
      </Box>

      {/* Informação de itens ocultos */}
      <Box
        sx={{
          minWidth: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {/* Lado esquerdo: botão de download */}
        <Box>
          <Button
            variant="outlined"
            size="small"
            onClick={() => handleExportExcel(`${tableName || "dados"}.xlsx`)}
          >
            Baixar tabela
          </Button>
        </Box>

        {/* Lado direito: informações de itens ocultos */}
        <Box>
          {countOculto > 0 && !showAll ? (
            <Typography
              sx={{ fontFamily: "Satoshi", fontSize: "16px", color: "#52525B" }}
            >
              {countOculto} item{countOculto > 1 ? "s" : ""} oculto
              {countOculto > 1 ? "s" : ""} (
              <span
                style={{ color: "#8142FC", cursor: "pointer" }}
                onClick={showEverything}
              >
                Exibir
              </span>
              )
            </Typography>
          ) : countOculto > 0 && showAll ? (
            <Typography
              sx={{ fontFamily: "Satoshi", fontSize: "16px", color: "#52525B" }}
            >
              {countOculto} item{countOculto > 1 ? "s" : ""} oculto
              {countOculto > 1 ? "s" : ""} (
              <span
                style={{ color: "#8142FC", cursor: "pointer" }}
                onClick={removeShowEverything}
              >
                Ocultar
              </span>
              )
            </Typography>
          ) : (
            <Typography
              sx={{ fontFamily: "Satoshi", fontSize: "16px", color: "#52525B" }}
            >
              Total de {activeRows.length} itens
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default SheetsTable;
